import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Save, Plus, X, ArrowUp, ArrowDown, Loader2, Clock, User, ListChecks, CalendarCheck } from 'lucide-react';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto',
  'America/Vancouver', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Dubai', 'Australia/Sydney',
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[--hc-text]">Settings</h2>
        <p className="text-sm text-[--hc-text-muted]">Manage your account and routine</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-[--hc-surface] w-fit flex-wrap">
        {[
          { key: 'profile', label: 'Profile', icon: User },
          { key: 'routine', label: 'Routine Editor', icon: ListChecks },
          { key: 'review', label: 'Weekly Review', icon: CalendarCheck },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key ? 'bg-white text-[--hc-primary] shadow-sm' : 'text-[--hc-text-muted] hover:text-[--hc-text]'
            }`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'routine' && <RoutineEditorTab />}
      {activeTab === 'review' && <WeeklyReviewTab />}
    </div>
  );
}

function ProfileTab() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [timezone, setTimezone] = useState(user?.timezone || 'America/New_York');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/profile', { name, email, timezone });
      updateUser(res.data.user);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[520px]" data-testid="settings-profile-form">
      <div className="rounded-xl border border-[--hc-border] bg-white p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[--hc-text] mb-1.5">Name</label>
          <input
            data-testid="settings-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[--hc-text] mb-1.5">Email</label>
          <input
            data-testid="settings-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[--hc-text] mb-1.5">Time Zone</label>
          <select
            data-testid="settings-timezone-select"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
          >
            {TIMEZONES.map(tz => (
              <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        <button
          data-testid="settings-save-button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[--hc-primary] text-white text-sm font-medium hover:bg-[--hc-primary-hover] active:scale-[0.98] transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function RoutineEditorTab() {
  const [routine, setRoutine] = useState(null);
  const [items, setItems] = useState([]);
  const [wakeTime, setWakeTime] = useState('06:00');
  const [startTime, setStartTime] = useState('08:00');
  const [newItemText, setNewItemText] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/routine/current').then(res => {
      const r = res.data.routine;
      if (r) {
        setRoutine(r);
        setItems(r.items || []);
        setWakeTime(r.wake_time || '06:00');
        setStartTime(r.start_operating_time || '08:00');
      }
    }).finally(() => setLoading(false));
  }, []);

  const addItem = () => {
    const text = newItemText.trim();
    if (!text) return;
    setItems(prev => [...prev, { id: `item-${Date.now()}`, text, grade: '+', position: prev.length, section: 'before' }]);
    setNewItemText('');
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));

  const setGrade = (id, grade) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, grade } : i));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    setItems(prev => {
      const newItems = [...prev];
      [newItems[idx - 1], newItems[idx]] = [newItems[idx], newItems[idx - 1]];
      return newItems;
    });
  };

  const moveDown = (idx) => {
    setItems(prev => {
      if (idx >= prev.length - 1) return prev;
      const newItems = [...prev];
      [newItems[idx], newItems[idx + 1]] = [newItems[idx + 1], newItems[idx]];
      return newItems;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const routineItems = items.map((item, idx) => ({
        id: item.id,
        text: item.text,
        grade: item.grade || '+',
        position: idx,
        section: item.section || 'before',
      }));
      await api.post('/routine/save', {
        items: routineItems,
        wake_time: wakeTime,
        start_operating_time: startTime,
      });
      toast.success('Routine updated. Changes take effect tomorrow.');
    } catch (err) {
      toast.error('Failed to save routine');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-[--hc-text-muted]">Loading routine...</div>;

  return (
    <div className="max-w-[600px]" data-testid="routine-editor">
      <div className="rounded-xl border border-[--hc-border] bg-white p-5">
        <p className="text-xs text-[--hc-warning] bg-[--hc-warning-soft] px-3 py-2 rounded-lg mb-4">
          Changes take effect starting tomorrow so your current day check-in is not disrupted.
        </p>

        {/* Add new item */}
        <div className="flex gap-2 mb-4">
          <input
            data-testid="routine-editor-add-input"
            type="text"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addItem()}
            className="flex-1 px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
            placeholder="Add new routine item..."
          />
          <button
            onClick={addItem}
            className="px-4 py-2.5 rounded-xl bg-[--hc-primary] text-white text-sm font-medium hover:bg-[--hc-primary-hover] active:scale-[0.98] transition-colors flex items-center gap-1.5"
          >
            <Plus size={16} /> Add
          </button>
        </div>

        {/* Items list */}
        <div className="space-y-2 mb-4">
          {items.map((item, idx) => (
            <div key={item.id} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white group">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveUp(idx)} className="text-[--hc-text-muted] hover:text-[--hc-text]"><ArrowUp size={12} /></button>
                <button onClick={() => moveDown(idx)} className="text-[--hc-text-muted] hover:text-[--hc-text]"><ArrowDown size={12} /></button>
              </div>
              <span className="text-xs text-[--hc-text-muted] w-5 tabular-nums">{idx + 1}.</span>
              <span className="flex-1 text-sm text-[--hc-text]">{item.text}</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setGrade(item.id, '+')}
                  className={`w-7 h-7 rounded text-xs font-bold transition-colors ${
                    item.grade === '+' ? 'bg-[--hc-success-soft] text-[--hc-success]' : 'bg-[--hc-surface] text-[--hc-text-muted]'
                  }`}
                >+</button>
                <button
                  onClick={() => setGrade(item.id, '/')}
                  className={`w-7 h-7 rounded text-xs font-bold transition-colors ${
                    item.grade === '/' ? 'bg-[--hc-surface-2] text-[--hc-text]' : 'bg-[--hc-surface] text-[--hc-text-muted]'
                  }`}
                >/</button>
                <button
                  onClick={() => setGrade(item.id, '-')}
                  className={`w-7 h-7 rounded text-xs font-bold transition-colors ${
                    item.grade === '-' ? 'bg-[--hc-danger-soft] text-[--hc-danger]' : 'bg-[--hc-surface] text-[--hc-text-muted]'
                  }`}
                >-</button>
              </div>
              <button onClick={() => removeItem(item.id)} className="text-[--hc-text-muted] hover:text-[--hc-danger] opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Times */}
        <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-[--hc-border]">
          <div>
            <label className="block text-sm font-medium text-[--hc-text] mb-1.5 flex items-center gap-1.5">
              <Clock size={14} /> Wake-up Time
            </label>
            <input
              type="time"
              value={wakeTime}
              onChange={(e) => setWakeTime(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[--hc-text] mb-1.5 flex items-center gap-1.5">
              <Clock size={14} /> Start Operating
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
            />
          </div>
        </div>

        <button
          data-testid="routine-editor-save-button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[--hc-primary] text-white text-sm font-medium hover:bg-[--hc-primary-hover] active:scale-[0.98] transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Routine'}
        </button>
      </div>
    </div>
  );
}

function WeeklyReviewTab() {
  const { user, updateUser } = useAuth();
  const [reviewDay, setReviewDay] = useState(user?.weekly_review_day || 'Sunday');
  const [saving, setSaving] = useState(false);

  // Weekly review form
  const [score, setScore] = useState(5);
  const [obstacles, setObstacles] = useState('');
  const [commitment, setCommitment] = useState('');
  const [reviewPending, setReviewPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/weekly-review/pending').then(res => setReviewPending(res.data)).catch(() => {});
  }, []);

  const handleDayChange = async (day) => {
    setReviewDay(day);
    setSaving(true);
    try {
      const res = await api.put('/profile', { weekly_review_day: day });
      updateUser(res.data.user);
      toast.success(`Review day set to ${day}`);
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReview = async () => {
    setSubmitting(true);
    try {
      await api.post('/weekly-review', {
        consistency_score: score,
        obstacles,
        commitment,
      });
      toast.success('Weekly review submitted!');
      setObstacles('');
      setCommitment('');
      setScore(5);
      setReviewPending(prev => prev ? { ...prev, already_submitted: true, pending: false } : null);
    } catch (err) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[520px] space-y-6">
      {/* Day setting */}
      <div className="rounded-xl border border-[--hc-border] bg-white p-5" data-testid="weekly-review-day-select">
        <h3 className="text-sm font-semibold text-[--hc-text] mb-1">Review Day</h3>
        <p className="text-xs text-[--hc-text-muted] mb-3">Choose which day you'll complete your weekly self-assessment.</p>
        <select
          value={reviewDay}
          onChange={(e) => handleDayChange(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring]"
          disabled={saving}
        >
          {DAYS.map(day => (
            <option key={day} value={day}>{day}</option>
          ))}
        </select>
      </div>

      {/* Weekly review form */}
      <div className="rounded-xl border border-[--hc-border] bg-white p-5">
        <h3 className="text-sm font-semibold text-[--hc-text] mb-1">Weekly Self-Assessment</h3>
        {reviewPending?.already_submitted ? (
          <p className="text-sm text-[--hc-success] mt-2">You've already submitted this week's review.</p>
        ) : (
          <div className="space-y-4 mt-3">
            <div>
              <label className="block text-sm font-medium text-[--hc-text] mb-2">
                How consistent were you this week?
              </label>
              <div className="flex items-center gap-3">
                <input
                  data-testid="weekly-review-score-slider"
                  type="range"
                  min={1}
                  max={10}
                  value={score}
                  onChange={(e) => setScore(parseInt(e.target.value))}
                  className="flex-1 accent-[--hc-accent]"
                />
                <span className="text-lg font-bold text-[--hc-primary] tabular-nums w-8 text-center">{score}</span>
              </div>
              <div className="flex justify-between text-xs text-[--hc-text-muted] mt-1">
                <span>Not consistent</span>
                <span>Very consistent</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[--hc-text] mb-1.5">
                What got in the way? <span className="text-[--hc-text-muted] font-normal">(optional)</span>
              </label>
              <textarea
                data-testid="weekly-review-obstacles-input"
                value={obstacles}
                onChange={(e) => setObstacles(e.target.value)}
                rows={3}
                className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring] resize-none"
                placeholder="Any obstacles or distractions..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[--hc-text] mb-1.5">
                What will you commit to next week? <span className="text-[--hc-text-muted] font-normal">(optional)</span>
              </label>
              <textarea
                data-testid="weekly-review-commitment-input"
                value={commitment}
                onChange={(e) => setCommitment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring] resize-none"
                placeholder="Your commitment for next week..."
              />
            </div>

            <button
              data-testid="weekly-review-submit-button"
              onClick={handleSubmitReview}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[--hc-primary] text-white text-sm font-medium hover:bg-[--hc-primary-hover] active:scale-[0.98] transition-colors disabled:opacity-50"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
